import { ObservableMap, computed, when } from 'mobx';
import type { ProcessAction, ProcessStage, ProcessStatus } from '../workflow/workflow-view/types';

import CacheStore from 'bz-utilities/modules/stores/cached-map-store';
import type { ProcessDto } from 'caseflow/api.dtos';
import { createTransformer } from 'mobx-utils';

export class ProcessCache extends CacheStore<ProcessDto> {
  getStatusesForProcess = createTransformer((processId: string) => {
    if (!processId || !this.loaded) {
      return [];
    }
    let process = this.processes.get(processId);
    let statuses: ProcessStatus[] = [];
    if (process) {
      for (let stage of process.stages) {
        statuses.push(...stage.statuses);
      }
    }
    return statuses;
  });

  constructor() {
    super('cf_process');
  }

  @computed
  get processes() {
    return this.all;
  }

  @computed
  get Current() {
    return this.All.filter((x) => x.current);
  }

  @computed get initialised(): boolean {
    return this.loaded;
  }

  @computed
  get stages(): ObservableMap<string, ProcessStage> {
    const map = new ObservableMap<string, ProcessStage>();
    this.All.forEach((process) => {
      const processStages = process.stages || [];
      const stageMap = processStages.map((stage) => [stage.id, stage] as [string, ProcessStage]);
      map.merge(stageMap);
    });
    return map;
  }

  @computed
  get statuses(): ObservableMap<string, ProcessStatus> {
    const map = new ObservableMap<string, ProcessStatus>();
    this.stages.forEach((stage) => {
      map.merge(stage.statuses.map((status) => [status.id, status] as [string, ProcessStatus]));
    });
    return map;
  }

  @computed
  get actions(): ObservableMap<string, ProcessAction> {
    const map = new ObservableMap<string, ProcessAction>();
    this.All.forEach((process) =>
      map
        .merge([[process.id, process.initialAction]])
        .merge(process.supplementaryActions.map((a) => [a.id, a] as [string, ProcessAction]))
    );
    this.statuses.forEach((status) =>
      map.merge(status.actions.map((a) => [a.id, a] as [string, ProcessAction]))
    );
    return map;
  }

  @computed
  get statusNames(): { statusId: string; statusName: string; processName: string }[] {
    const summaries: { statusId: string; statusName: string; processName: string }[] = [];

    this.All.forEach((process) => {
      process.stages?.forEach((stage) => {
        stage.statuses?.forEach((status) => {
          summaries.push({
            statusId: status.id,
            statusName: status.name,
            processName: process.name
          });
        });
      });
    });

    return summaries;
  }

  public async getStatusByCode(
    code: string,
    processId: string
  ): Promise<ProcessStatus | undefined> {
    await when(() => this.loaded);
    return this.getStatusesForProcess(processId).find((x) => x.code === code);
  }
}
